import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BookOpen, 
  FileText, 
  Download, 
  ExternalLink, 
  Search,
  BookMarked,
  GraduationCap,
  Globe,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  FileQuestion,
  File
} from 'lucide-react';
import { useResources, useFAQ } from '../lib/hooks';
import { resourcesApi } from '../lib/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useLanguage } from '../contexts/LanguageContext';

const ResourcesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { lang } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [selectedType, setSelectedType] = useState('all');

  const { data: resources = [], isLoading: resourcesLoading } = useResources();
  const { data: faqGroups = {}, isLoading: faqLoading } = useFAQ();

  const loading = resourcesLoading || faqLoading;

  // Get unique categories from resources
  const categories = ['Tous', ...new Set(resources.filter(r => r.type !== 'faq').map(r => r.category).filter(Boolean))];

  // Filter guides and templates
  const filteredResources = resources
    .filter(r => r.type !== 'faq')
    .filter(r => {
      const matchesSearch = r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (r.content || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'Tous' || r.category === selectedCategory;
      const matchesType = selectedType === 'all' || r.type === selectedType;
      return matchesSearch && matchesCategory && matchesType;
    });

  // Static FAQ from landing page
  const staticFAQs = [
    {
      question: "Est-ce que Speak&Move est une arnaque ?",
      answer: "Non, Speak&Move n'est pas une arnaque. Nous sommes une structure transparente, immatriculée en France avec une filiale au Cameroun, et nous accompagnons des étudiants d'Afrique francophone dans leur projet d'études en Allemagne avec une méthode claire, des étapes définies et un suivi réel. Nous ne promettons rien que nous ne pouvons pas prouver. Chaque dossier est traité avec sérieux, et nous proposons toujours un échange direct (appel, visio ou rencontre) pour présenter les résultats, répondre aux questions et clarifier chaque étape du parcours."
    },
    {
      question: "Combien ça coûte au total ?",
      answer: "Formation complète (A1 à C1) : à partir de 500 000 FCFA. Montage du dossier visa : à partir de 150 000 FCFA. Paiement flexible : possibilité de payer en 3 à 6 fois, sans frais. Après votre test de niveau gratuit, nous vous remettons un devis précis et personnalisé, sans aucun engagement."
    },
    {
      question: "Que se passe-t-il si mon visa est refusé ?",
      answer: "En cas de refus de visa, nous vous accompagnons pour comprendre la décision et préparer une nouvelle demande si vous le souhaitez. Nos frais couvrent le travail réalisé sur votre dossier, mais nous restons à vos côtés pour analyser les motifs du refus et vous proposer la meilleure suite possible."
    },
    {
      question: "Est-ce que je peux travailler en Allemagne avec un visa étudiant ?",
      answer: "Oui, vous pouvez travailler en Allemagne avec un visa étudiant. La loi allemande autorise les étudiants internationaux à travailler jusqu'à 120 jours à temps plein par an, ou 240 jours à temps partiel. C'est idéal pour financer une partie de vos dépenses ou acquérir une première expérience professionnelle."
    },
    {
      question: "Je n'ai aucune base en allemand, c'est possible ?",
      answer: "Oui, c'est tout à fait possible. La majorité de nos étudiants commencent au niveau zéro, et progressent rapidement grâce à une formation intensive, un suivi régulier et une méthode adaptée aux débutants. Avec de la régularité et un bon encadrement, vous pouvez atteindre le niveau requis pour étudier ou travailler en Allemagne."
    },
    {
      question: "Pourquoi l'Allemagne et pas la France ou le Canada ?",
      answer: "L'Allemagne offre aujourd'hui l'un des meilleurs rapports qualité-opportunités au monde : des études quasiment gratuites (même pour les étrangers), un marché du travail en forte demande, un visa étudiant flexible qui permet de travailler pendant les études, un taux d'employabilité très élevé après le diplôme, et une voie claire vers la résidence permanente."
    },
    {
      question: "Comment se déroule l'alternance ('Ausbildung') avec vos partenaires ?",
      answer: "Nos partenaires en Allemagne proposent des programmes de formation en alternance ('Ausbildung'), reconnus par l'État allemand. Ces programmes permettent aux étudiants de se former tout en travaillant, avec un salaire mensuel et une forte probabilité d'embauche à la fin. Speak&Move vous prépare et vous accompagne, nos partenaires vous forment et vous emploient."
    }
  ];

  // Filter FAQ (combine static + dynamic)
  const filteredStaticFAQs = staticFAQs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFAQ = {};
  Object.entries(faqGroups).forEach(([category, faqs]) => {
    const filtered = faqs.filter(faq => 
      faq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (faq.content || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredFAQ[category] = filtered;
    }
  });

  const getResourceIcon = (type, category) => {
    if (type === 'template') return File;
    
    const iconMap = {
      'Examens': FileText,
      'Vocabulaire': BookOpen,
      'Grammaire': BookMarked,
      'Culture': Globe,
      'Études': GraduationCap,
    };
    return iconMap[category] || FileText;
  };

  const handleDownload = (resource) => {
    if (resource.file_url) {
      window.open(resourcesApi.getFileUrl(resource.id), '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resources-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Ressources</h1>
        <p className="text-muted-foreground mt-1">Documents et supports pour votre apprentissage</p>
      </div>

      {/* Search and Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une ressource..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="resource-search-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={selectedType === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedType('all')}
                  className={selectedType === 'all' ? 'btn-active' : ''}
                >
                  Tout
                </Button>
                <Button
                  variant={selectedType === 'guide' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedType('guide')}
                  className={selectedType === 'guide' ? 'btn-active' : ''}
                >
                  Guides
                </Button>
                <Button
                  variant={selectedType === 'template' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedType('template')}
                  className={selectedType === 'template' ? 'btn-active' : ''}
                >
                  Modèles
                </Button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? 'btn-active' : ''}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resources Grid */}
      {filteredResources.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => {
            const Icon = getResourceIcon(resource.type, resource.category);
            return (
              <Card 
                key={resource.id} 
                className="border-0 shadow-sm card-hover cursor-pointer"
                onClick={() => handleDownload(resource)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      resource.type === 'template' ? 'bg-accent/10' : 'bg-secondary/10'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        resource.type === 'template' ? 'text-accent' : 'text-secondary'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{resource.title}</h3>
                      {resource.content && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {resource.content}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                          {resource.category}
                        </span>
                        {resource.file_url && (
                          <Button variant="ghost" size="sm" className="h-8 px-2">
                            <Download className="w-4 h-4 mr-1" />
                            {resource.type === 'guide' ? 'PDF' : 'Télécharger'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {filteredResources.length === 0 && selectedType !== 'all' && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12">
            <div className="text-center">
              <BookMarked className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-xl font-semibold mb-2">Aucune ressource trouvée</h3>
              <p className="text-muted-foreground">
                Essayez de modifier vos critères de recherche
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section - Static from Landing Page */}
      {filteredStaticFAQs.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            Questions fréquentes
          </h2>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="space-y-2">
                {filteredStaticFAQs.map((faq, index) => (
                  <AccordionItem key={`static-faq-${index}`} value={`static-faq-${index}`} className="border rounded-lg px-4">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="font-medium">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {filteredResources.length === 0 && filteredStaticFAQs.length === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12">
            <div className="text-center">
              <BookMarked className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-xl font-semibold mb-2">Aucune ressource disponible</h3>
              <p className="text-muted-foreground">
                Les ressources seront bientôt disponibles. Revenez plus tard !
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-secondary/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <h4 className="font-semibold">Nouvelles ressources chaque semaine</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Notre équipe pédagogique ajoute régulièrement de nouveaux supports pour vous aider 
                dans votre apprentissage de l'allemand. N'hésitez pas à revenir régulièrement !
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResourcesPage;
